import weightedRandom from './weightedRandom'

import type { CloudFlareExperiment } from '../index'
import type { Condition, Flow, Context, Experiment } from '../models/types'

//* Non-async version for client-side
export function generatePartialUserData({
  context,
  flow,
  providedUserData,
  searchParams,
}: {
  context: Context
  flow: Flow
  providedUserData?: { [key: string]: string }
  searchParams: URLSearchParams
}): {
  userData: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
} {
  if (context.isServerSide) {
    throw new Error('Use asyncGeneratePartialUserData for server-side calls')
  }

  const { userDataWithoutDefaults } = processInitialUserData({
    context,
    flow,
    providedUserData,
    searchParams,
  })

  return finalizeUserData(userDataWithoutDefaults, flow)
}

//* Async version for server-side
export async function asyncGeneratePartialUserData({
  context,
  flow,
  providedUserData,
  projectId,
  searchParams,
}: {
  context: Context
  flow: Flow
  providedUserData?: { [key: string]: string }
  projectId: string
  searchParams: URLSearchParams
}): Promise<{
  userData: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
}> {
  const { userDataWithoutDefaults } = processInitialUserData({
    context,
    flow,
    providedUserData,
    searchParams,
  })

  //* Get experiments from experiments worker
  const connectedExperiments = await getConnectedExperiments({
    flow,
    projectId,
    context,
  })

  //* Create conditions for connected experiments
  const connectedExperimentConditions = connectedExperiments.reduce(
    (acc: Condition[], experiment) => {
      //* If the experiment is running, create a condition by considering the variants and their weights
      if (experiment.status === 'running') {
        const condition = createExperimentConditions(experiment, undefined, context, {
          projectId,
          flowId: flow.id,
        })
        acc.push(condition)
      }

      //* If the experiment is paused, use the paused_showing_variant as the forced value
      if (experiment.status === 'paused' && experiment.paused_showing_variant) {
        const condition = createExperimentConditions(
          experiment,
          experiment.paused_showing_variant,
          context,
          { projectId, flowId: flow.id }
        )
        acc.push(condition)
      }

      //* If the experiment is winner_picked, use the winner_variant as the forced value
      if (experiment.status === 'winner_picked' && experiment.winner_variant) {
        const condition = createExperimentConditions(
          experiment,
          experiment.winner_variant,
          context,
          { projectId, flowId: flow.id }
        )
        acc.push(condition)
      }

      return acc
    },
    []
  )

  //* Insert into user data without defaults the connected experiments
  const userDataWithConnectedExperimentsWithoutDefaults = connectedExperimentConditions.reduce(
    (acc: { [key: string]: string }, condition) => {
      acc[condition.key] = providedUserData?.[condition.key] || `${condition.values[0]}`
      return acc
    },
    userDataWithoutDefaults
  )

  return finalizeUserData(userDataWithConnectedExperimentsWithoutDefaults, flow)
}

//* Function to process the initial user data creation
function processInitialUserData({
  context,
  flow,
  providedUserData,
  searchParams,
}: {
  context: Context
  flow: Flow
  providedUserData?: { [key: string]: string }
  searchParams: URLSearchParams
}): {
  userDataWithoutDefaults: { [key: string]: string }
  generatedConditions: Condition[]
} {
  //* Initialize user data without defaults
  let userDataWithoutDefaults: any = {}

  //* If there are url_keys, add them to user data without defaults
  if (flow.url_keys) {
    //* Transform to accept both arrays and strings separated by new lines
    const urlKeys = Array.isArray(flow.url_keys)
      ? flow.url_keys
      : flow.url_keys.split('\n').map((t) => t.trim())

    //* Add the url keys to user data without defaults if they exist in the provided user data
    urlKeys.forEach((key) => {
      if (providedUserData?.[key]) {
        try {
          userDataWithoutDefaults[key] =
            typeof providedUserData[key] === 'string'
              ? providedUserData[key]
              : JSON.stringify(providedUserData[key])
        } catch (error) {
          userDataWithoutDefaults[key] = providedUserData[key]
        }
      }
    })
  }

  //* If there are embed_code_keys, add them to user data without defaults
  if (flow.embed_code_keys) {
    //* Transform to accept both arrays and strings separated by new lines
    const embedCodeKeys = Array.isArray(flow.embed_code_keys)
      ? flow.embed_code_keys
      : flow.embed_code_keys.split('\n').map((t) => t.trim())

    //* Add the embed code keys to user data without defaults if they exist in the provided user data
    embedCodeKeys.forEach((key) => {
      if (providedUserData?.[key]) {
        try {
          userDataWithoutDefaults[key] =
            typeof providedUserData[key] === 'string'
              ? JSON.stringify(providedUserData[key])
              : providedUserData[key]
        } catch (error) {
          userDataWithoutDefaults[key] = providedUserData[key]
        }
      }
    })
  }

  //* Always include _show_hidden_pages if it exists in providedUserData
  if (providedUserData?.['_show_hidden_pages']) {
    userDataWithoutDefaults['_show_hidden_pages'] = providedUserData['_show_hidden_pages']
  }

  //* Define experimentPresets as the preset values from the search params
  const flowExperiments = flow.experiments || []
  const experimentKeys = new Set(flowExperiments.map((e) => e.key))
  const experimentPresets: [string, string][] = []
  for (let [key, value] of searchParams) {
    if (experimentKeys.has(key)) experimentPresets.push([key, value])
  }

  //* Define connectedExperimentPresets as the preset values from the connected experiments
  const connectedExperiments = flow.connected_experiments || []
  const connectedExperimentKeys = new Set(connectedExperiments.map((e) => e.experiment_key))
  const connectedExperimentPresets: [string, string][] = []
  for (let [key, value] of searchParams) {
    if (connectedExperimentKeys.has(key)) connectedExperimentPresets.push([key, value])
  }

  //* Create preset conditions from the experiment presets and connected experiment presets
  const presetConditions: Condition[] = [...experimentPresets, ...connectedExperimentPresets].map(
    ([key, value]) => ({
      key,
      operator: '==',
      values: [value],
    })
  )

  //* Define seenConditions as the keys of the preset conditions (experiments keys in search params)
  const seenConditions = new Set(presetConditions.map(({ key }) => key))

  //* Generate conditions for the flow experiments that are live and have not been preset
  const generatedConditions = flowExperiments.reduce((acc: Condition[], experiment) => {
    if (experiment.status !== 'live' || seenConditions.has(experiment.key)) return acc

    const condition = createExperimentConditions(experiment, undefined, context, {
      projectId: flow.project_id,
      flowId: flow.id,
    })
    acc.push(condition)
    return acc
  }, presetConditions)

  //* Insert into user data without defaults the flow experiments
  userDataWithoutDefaults = generatedConditions.reduce(
    (acc: { [key: string]: string }, condition) => {
      acc[condition.key] = `${condition.values[0]}`
      return acc
    },
    userDataWithoutDefaults
  )

  return {
    userDataWithoutDefaults,
    generatedConditions,
  }
}

//* Function to finalize the initialization of the user data
function finalizeUserData(
  userDataWithoutDefaults: { [key: string]: string },
  flow: Flow
): {
  userData: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
} {
  //* Merge the default user data with the user data without defaults
  const defaultUserData = flow.defaults?.userData || {}
  const userData = { ...defaultUserData, ...userDataWithoutDefaults }

  //* Return the user data and the user data without defaults
  return {
    userData,
    userDataWithoutDefaults,
  }
}

function createExperimentConditions(
  experiment: Experiment,
  forcedValue?: string,
  context?: Context,
  metadata?: {
    projectId?: string
    flowId?: string
  }
) {
  const logger = context?.logger

  const variants = experiment.variants || []
  const weights = variants.map((variant) => ({
    key: variant.key,
    weight: variant.weighting === undefined ? 1 : variant.weighting,
  }))

  //* If forcedValue is provided use it
  if (forcedValue) {
    logger?.info({
      scope: 'Get partial userdata',
      functionName: 'createExperimentConditions',
      message: 'Creating experiment condition with forced value',
      props: {
        project_id: metadata?.projectId,
        flow_id: metadata?.flowId,
        experiment_id: experiment.id,
        experiment_key: experiment.key,
        forced_value: forcedValue,
      },
    })

    //* If forcedValue exists in variants, use it, otherwise default to control_no_change
    if (!variants.some((variant) => variant.key === forcedValue)) forcedValue = 'control_no_change'

    return { key: experiment.key, operator: '==', values: [forcedValue] }
  }

  const value = weightedRandom(weights || [])

  logger?.info({
    scope: 'Get partial userdata',
    functionName: 'createExperimentConditions',
    message: 'Creating experiment condition with weighted random',
    props: {
      project_id: metadata?.projectId,
      flow_id: metadata?.flowId,
      experiment_id: experiment.id,
      experiment_key: experiment.key,
      weights,
      value,
    },
  })

  return { key: experiment.key, operator: '==', values: [value] }
}

async function getConnectedExperiments({
  flow,
  projectId,
  context,
}: {
  flow: Flow
  projectId: string
  context: Context
}): Promise<CloudFlareExperiment[]> {
  const logger = context.logger

  const EXPERIMENTS_WORKER = context.environment?.EXPERIMENTS_WORKER
  let connectedExperiments: CloudFlareExperiment[] = []

  try {
    if (!EXPERIMENTS_WORKER) throw new Error('EXPERIMENTS_WORKER is not available')

    const experimentsIds =
      flow.connected_experiments?.map((experiment) => experiment.experiment_id) || []

    connectedExperiments = await EXPERIMENTS_WORKER.getExperiments({
      projectId,
      experimentsIds,
    })

    logger?.info({
      scope: 'Get partial userdata',
      functionName: 'getConnectedExperiments',
      message: 'Connected experiments',
      props: {
        connectedExperiments,
        project_id: projectId,
        flow_id: flow.id,
        experiments_ids: experimentsIds,
      },
    })

    //* Filter out experiments that are not running, paused, or winner_picked
    return connectedExperiments.filter(
      (experiment) =>
        experiment.status === 'running' ||
        experiment.status === 'paused' ||
        experiment.status === 'winner_picked'
    )
  } catch (error) {
    logger?.error({
      scope: 'Get partial userdata',
      functionName: 'getConnectedExperiments',
      message: 'Error getting connected experiments',
      props: {
        error: typeof error === 'object' ? JSON.stringify(error) : error,
        project_id: projectId,
        flow_id: flow.id,
      },
    })
    return []
  }
}
